import {
  AuthFlowType,
  ChallengeNameType,
  CognitoIdentityProviderClient,
  ConfirmSignUpCommand,
  GlobalSignOutCommand,
  InitiateAuthCommand,
  RespondToAuthChallengeCommand,
  SignUpCommand,
} from '@aws-sdk/client-cognito-identity-provider'
import { v4 as uuidv4 } from 'uuid'
import { cognitoConfig, isFeatureEnabled } from '../config/app'

// Create Cognito client for custom flows
const cognitoClient = new CognitoIdentityProviderClient({
  region: cognitoConfig.region,
})

// Custom authentication flow types
export interface OTPSignUpParams {
  username: string // email or phone number
}

export interface OTPConfirmSignUpParams {
  generatedUsername: string
  confirmationCode: string
  session: string
  originalUsername: string
}

export interface RefreshTokenParams {
  username: string
  refreshToken: string
}

export interface CustomAuthParams {
  username: string // email or phone number
}

export interface CustomAuthChallengeParams {
  session: string
  deliveryMedium: 'email' | 'phone_number' | 'UNKNOWN'
  username: string
}

export interface CustomAuthOTPParams {
  session: string
  otpCode: string
  username: string
}

export interface AuthFlowResult {
  success: boolean
  data?: any
  error?: string
  session?: string
  challengeName?: string
  challengeParameters?: any
  tokens?: {
    accessToken?: string
    idToken?: string
    refreshToken?: string
  }
}

// Utility functions
const isEmail = (username: string): boolean => {
  return username.includes('@')
}

const isPhoneNumber = (username: string): boolean => {
  return username.startsWith('+')
}

const getDeliveryMedium = (username: string): 'email' | 'phone_number' | 'UNKNOWN' => {
  if (isEmail(username)) return 'email'
  if (isPhoneNumber(username)) return 'phone_number'
  return 'UNKNOWN'
}

// Custom authentication service
export const customAuthService = {
  // OTP Sign Up Flow - Exact implementation per specifications
  // generated_username = str(uuid.uuid4())
  // response = client.sign_up(ClientId=client_id, Username=generated_username, UserAttributes=[...])
  // session: str = response.get('Session', '')
  async otpSignUp({ username }: OTPSignUpParams): Promise<AuthFlowResult> {
    if (!isFeatureEnabled('enableCognito')) {
      return { success: false, error: 'Authentication is not enabled' }
    }

    try {
      // Step 1: Generate UUID username (as per spec)
      const generatedUsername = uuidv4()

      // Step 2: Determine user attributes based on username format (as per spec)
      const userAttributes = []
      if (isPhoneNumber(username)) {
        userAttributes.push({ Name: 'phone_number', Value: username })
      } else if (isEmail(username)) {
        userAttributes.push({ Name: 'email', Value: username })
      } else {
        return { success: false, error: 'Invalid username format. Must be email or phone number.' }
      }

      // Step 3: Sign up with generated username (as per spec)
      const command = new SignUpCommand({
        ClientId: cognitoConfig.userPoolWebClientId,
        Username: generatedUsername,
        UserAttributes: userAttributes,
      })

      const response = await cognitoClient.send(command)
      // Step 4: Extract session (as per spec)
      const session = response.Session || ''

      return {
        success: true,
        data: {
          generatedUsername,
          session,
          userSub: response.UserSub,
        },
        session,
      }
    } catch (error: any) {

      // Extract more detailed error information
      let errorMessage = error.message || 'Failed to initiate OTP sign up'
      if (error.name) {
        errorMessage = `${error.name}: ${errorMessage}`
      }

      return {
        success: false,
        error: errorMessage,
      }
    }
  },

  // OTP Confirm Sign Up Flow - Exact implementation per specifications
  // confirm_response = client.confirm_sign_up(ClientId=client_id, Username=generated_username, ConfirmationCode=code, Session=session)
  // initiateAuthResponse = client.initiate_auth(ClientId=client_id, AuthFlow='USER_AUTH', Session=confirm_response["Session"], AuthParameters={'USERNAME': username})
  async otpConfirmSignUp({
    generatedUsername,
    confirmationCode,
    session,
    originalUsername,
  }: OTPConfirmSignUpParams): Promise<AuthFlowResult> {
    if (!isFeatureEnabled('enableCognito')) {
      return { success: false, error: 'Authentication is not enabled' }
    }

    try {

      // Step 1: Confirm the sign up (as per spec)
      const confirmCommand = new ConfirmSignUpCommand({
        ClientId: cognitoConfig.userPoolWebClientId,
        Username: generatedUsername,
        ConfirmationCode: confirmationCode,
        Session: session,
      })

      const confirmResponse = await cognitoClient.send(confirmCommand)
      const newSession = confirmResponse.Session || session

      // Step 2: Initiate auth with USER_AUTH flow using ORIGINAL username (as per spec)
      const authCommand = new InitiateAuthCommand({
        ClientId: cognitoConfig.userPoolWebClientId,
        AuthFlow: AuthFlowType.USER_AUTH,
        Session: newSession,
        AuthParameters: {
          USERNAME: originalUsername, // Using original username as per spec
        },
      })

      const authResponse = await cognitoClient.send(authCommand)

      return {
        success: true,
        data: authResponse,
        session: authResponse.Session,
        tokens: {
          accessToken: authResponse.AuthenticationResult?.AccessToken,
          idToken: authResponse.AuthenticationResult?.IdToken,
          refreshToken: authResponse.AuthenticationResult?.RefreshToken,
        },
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to confirm OTP sign up',
      }
    }
  },

  // Refresh Token Auth Flow
  async refreshTokenAuth({ username, refreshToken }: RefreshTokenParams): Promise<AuthFlowResult> {
    if (!isFeatureEnabled('enableCognito')) {
      return { success: false, error: 'Authentication is not enabled' }
    }

    try {
      const command = new InitiateAuthCommand({
        ClientId: cognitoConfig.userPoolWebClientId,
        AuthFlow: AuthFlowType.REFRESH_TOKEN_AUTH,
        AuthParameters: {
          USERNAME: username,
          REFRESH_TOKEN: refreshToken,
        },
      })

      const response = await cognitoClient.send(command)

      return {
        success: true,
        data: response,
        tokens: {
          accessToken: response.AuthenticationResult?.AccessToken,
          idToken: response.AuthenticationResult?.IdToken,
          refreshToken: response.AuthenticationResult?.RefreshToken,
        },
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to refresh token',
      }
    }
  },

  // Custom Auth Flow - Step 1: Initiate Auth
  async customAuthInitiate({ username }: CustomAuthParams): Promise<AuthFlowResult> {
    if (!isFeatureEnabled('enableCognito')) {
      return { success: false, error: 'Authentication is not enabled' }
    }

    try {
      const command = new InitiateAuthCommand({
        ClientId: cognitoConfig.userPoolWebClientId,
        AuthFlow: AuthFlowType.CUSTOM_AUTH,
        AuthParameters: {
          USERNAME: username,
        },
      })

      const response = await cognitoClient.send(command)

      return {
        success: true,
        data: response,
        session: response.Session,
        challengeName: response.ChallengeName,
        challengeParameters: response.ChallengeParameters,
      }
    } catch (error: any) {

      // Extract more detailed error information
      let errorMessage = error.message || 'Failed to initiate custom auth'
      if (error.name) {
        errorMessage = `${error.name}: ${errorMessage}`
      }

      return {
        success: false,
        error: errorMessage,
      }
    }
  },

  // Custom Auth Flow - Step 2: Respond to Challenge (Delivery Medium)
  async customAuthChallenge({
    session,
    deliveryMedium,
    username,
  }: CustomAuthChallengeParams): Promise<AuthFlowResult> {
    if (!isFeatureEnabled('enableCognito')) {
      return { success: false, error: 'Authentication is not enabled' }
    }

    try {
      const command = new RespondToAuthChallengeCommand({
        ClientId: cognitoConfig.userPoolWebClientId,
        ChallengeName: ChallengeNameType.CUSTOM_CHALLENGE,
        ClientMetadata: {
          deliveryMedium: deliveryMedium,
        },
        ChallengeResponses: {
          ANSWER: deliveryMedium,
          USERNAME: username,
        },
        Session: session,
      })

      const response = await cognitoClient.send(command)

      return {
        success: true,
        data: response,
        session: response.Session,
        challengeName: response.ChallengeName,
        challengeParameters: response.ChallengeParameters,
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to respond to auth challenge',
      }
    }
  },

  // Custom Auth Flow - Step 3: Respond to OTP Challenge
  async customAuthOTP({
    session,
    otpCode,
    username,
  }: CustomAuthOTPParams): Promise<AuthFlowResult> {
    if (!isFeatureEnabled('enableCognito')) {
      return { success: false, error: 'Authentication is not enabled' }
    }

    try {

      const command = new RespondToAuthChallengeCommand({
        ClientId: cognitoConfig.userPoolWebClientId,
        ChallengeName: ChallengeNameType.CUSTOM_CHALLENGE,
        ChallengeResponses: {
          ANSWER: otpCode,
          USERNAME: username,
        },
        Session: session,
      })

      const response = await cognitoClient.send(command)

      const tokens = {
        accessToken: response.AuthenticationResult?.AccessToken,
        idToken: response.AuthenticationResult?.IdToken,
        refreshToken: response.AuthenticationResult?.RefreshToken,
      }


      return {
        success: true,
        data: response,
        session: response.Session,
        tokens,
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to verify OTP code',
      }
    }
  },

  // Complete Custom Auth Flow (convenience method)
  async customAuthComplete({
    username,
    otpCode,
  }: {
    username: string
    otpCode: string
  }): Promise<AuthFlowResult> {
    try {

      // Step 1: Initiate auth
      const initiateResult = await this.customAuthInitiate({ username })
      if (!initiateResult.success) {
        return initiateResult
      }

      // Step 2: Respond to challenge with delivery medium
      const deliveryMedium = getDeliveryMedium(username)
      const challengeResult = await this.customAuthChallenge({
        session: initiateResult.session!,
        deliveryMedium,
        username,
      })
      if (!challengeResult.success) {
        return challengeResult
      }

      // Step 3: Respond to OTP challenge
      const otpResult = await this.customAuthOTP({
        session: challengeResult.session!,
        otpCode,
        username,
      })

      return otpResult
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to complete custom auth flow',
      }
    }
  },

  // Sign out user from all devices
  async signOut(accessToken: string): Promise<{ success: boolean; error?: string }> {
    try {
      if (!isFeatureEnabled('enableCognito')) {
        return { success: true }
      }


      const command = new GlobalSignOutCommand({
        AccessToken: accessToken,
      })

      await cognitoClient.send(command)

      return { success: true }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to sign out',
      }
    }
  },
}

// Export utility functions
export { getDeliveryMedium, isEmail, isPhoneNumber }
